import { AlertTriangle, CheckCircle2, CircleDashed } from 'lucide-react';

import { formatarData } from '@/nucleo/formato/datas';
import { STATUS_TONE } from '@/nucleo/statusBadgeTokens';
import { cn } from '@/nucleo/utilitarios';
import { situacaoDeValidade } from '@/nucleo/validade';
import { Badge } from '@/ui/base/badge';

interface DocValidadeBadgeProps {
  validade: string | null | undefined;
  /** Texto mostrado quando não há data informada. Default: "Não informado". */
  emptyLabel?: string;
  /** Esconde completamente quando vazio. */
  hideWhenEmpty?: boolean;
  className?: string;
}

/**
 * Badge padronizado para datas de validade de documentos.
 *
 * Portado do protótipo (`components/DocValidadeBadge.tsx`), usando
 * `situacaoDeValidade` (nucleo/validade.ts) em vez do `getDocValidadeStatus`
 * do protótipo — mesma janela de 30 dias, já a fonte única de verdade deste
 * repositório (usada por `IrregularidadeCell`, `CondutoresPage`, etc.).
 *
 *  - Vencido       → `STATUS_TONE.destructive` · "Vencido DD/MM/AAAA"
 *  - Vence em ≤30d → `STATUS_TONE.warning`     · "Vence DD/MM/AAAA"
 *  - Vigente       → `STATUS_TONE.success`     · "Vigente DD/MM/AAAA"
 *  - Sem data      → `STATUS_TONE.neutral`     · "Não informado"
 */
export function DocValidadeBadge({
  validade,
  emptyLabel = 'Não informado',
  hideWhenEmpty,
  className,
}: DocValidadeBadgeProps) {
  const situacao = situacaoDeValidade(validade);

  if (situacao === null) {
    if (hideWhenEmpty) {
      return null;
    }
    return (
      <Badge variant="outline" className={cn('gap-1 font-medium', STATUS_TONE.neutral, className)}>
        <CircleDashed className="h-3 w-3" aria-hidden="true" /> {emptyLabel}
      </Badge>
    );
  }

  const formatado = formatarData(validade);

  if (situacao === 'vencido') {
    return (
      <Badge
        variant="outline"
        className={cn('gap-1 font-medium', STATUS_TONE.destructive, className)}
      >
        <AlertTriangle className="h-3 w-3" aria-hidden="true" /> Vencido {formatado}
      </Badge>
    );
  }
  if (situacao === 'vencendo') {
    return (
      <Badge variant="outline" className={cn('gap-1 font-medium', STATUS_TONE.warning, className)}>
        <AlertTriangle className="h-3 w-3" aria-hidden="true" /> Vence {formatado}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className={cn('gap-1 font-medium', STATUS_TONE.success, className)}>
      <CheckCircle2 className="h-3 w-3" aria-hidden="true" /> Vigente {formatado}
    </Badge>
  );
}
